import { TabProps } from './tab.interface';
import classNames from 'classnames';
import { useBreakpoint } from 'src/hooks/use-breakpoint';

/**
 * Tab component displays a simple icon with border highlighting when active.
 *
 * @param {string} props.icon - Icon displayed in the tab.
 * @param {string} props.label - Text displayed in the tab. Optional.
 * @param {boolean} props.isSelected - Is tab selected.
 * @param {() => void} props.onClick - On tab clicked.
 * @param {boolean} props.showWhileOnboarding - Whether the tab should be shown while onboarding.
 *
 * @returns {JSX.Element} The Tab component.
 */
const Tab: React.FC<TabProps> = ({
  icon,
  label,
  ariaLabel,
  isSelected,
  onClick,
  showWhileOnboarding = false,
  colorScheme = 'default',
  notificationCount = 0,
}) => {
  const { isMobile } = useBreakpoint();
  const visibleNotificationCount = Number.isFinite(notificationCount) ? Math.max(0, Math.floor(notificationCount)) : 0;
  const notificationLabel = visibleNotificationCount > 99 ? '99+' : String(visibleNotificationCount);

  return (
    <div
      className={classNames('relative flex min-w-0 justify-center items-center', {
        'flex-1': isMobile,
        'shrink-0': !isMobile && !label,
        'w-full': !isMobile && !!label,
        'md:z-[100]': showWhileOnboarding,
      })}
    >
      <button
        type="button"
        aria-label={`${ariaLabel || label}${visibleNotificationCount > 0 ? `, ${visibleNotificationCount} new` : ''}`}
        aria-pressed={isSelected}
        className={classNames(
          'relative group flex items-center justify-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ease-out cursor-pointer',
          {
            'h-12 w-12': !label && !isMobile,
            'w-full min-w-0': !!label || isMobile,
            'p-3': !label,
            'h-12': label && !isMobile,
            'z-10': showWhileOnboarding,
            'md:px-5 md:py-4': label,
            'h-16': isMobile,
            'px-1': isMobile,
            'py-2': isMobile,
            'rounded-2xl': isMobile,
            'justify-center': isMobile,
          }
        )}
        onClick={onClick}
      >
        {visibleNotificationCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute right-0.5 top-0.5 z-30 inline-flex min-w-5 items-center justify-center md:right-1 md:top-1"
          >
            <span className="absolute h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70 motion-reduce:animate-none" />
            <span className="relative inline-flex h-5 min-w-5 animate-pulse items-center justify-center rounded-full bg-violet-600 px-1 font-manrope text-[10px] font-extrabold leading-none text-white shadow-md ring-2 ring-emerald-300 motion-reduce:animate-none">
              {notificationLabel}
            </span>
          </span>
        )}

        {/* Background with border styling */}
        <div
          className={classNames('absolute inset-0 transition-all duration-300 ease-out', {
            'rounded-lg': !isMobile,
            'rounded-2xl': isMobile,
            // Active state with border
            'bg-white/90 backdrop-blur-sm border-2 border-blue-500 shadow-md': isSelected && colorScheme === 'default',
            'bg-violet-100/80 backdrop-blur-sm border-2 border-violet-500 shadow-md':
              isSelected && colorScheme === 'purple',
            // Inactive state with no border, just background
            'bg-white/80 backdrop-blur-sm': !isSelected,
          })}
        />

        {/* Content container */}
        <div
          className={classNames('relative z-10 flex items-center justify-center transition-all duration-300', {
            'gap-4': label && !isMobile,
            'flex-col gap-1': isMobile,
            'gap-0': !label && !isMobile,
          })}
        >
          {/* Icon with styling */}
          <div
            className={classNames('transition-all duration-300 flex items-center justify-center', {
              'flex-shrink-0': isMobile,
              'w-7 h-7': isMobile,
              // Active state colors
              'text-blue-600': isSelected && colorScheme === 'default',
              'text-violet-700': isSelected && colorScheme === 'purple',
              // Inactive state colors
              'text-gray-500': !isSelected,
            })}
          >
            {icon}
          </div>

          {label && (
            <span
              className={classNames('font-medium transition-all duration-300', {
                'h-6 max-w-full overflow-hidden break-words text-center text-[10px] leading-3': isMobile,
                'text-sm md:text-base': !isMobile,
                'text-blue-600 font-semibold': isSelected && colorScheme === 'default',
                'text-violet-700 font-semibold': isSelected && colorScheme === 'purple',
                'text-gray-600': !isSelected,
              })}
            >
              {label}
            </span>
          )}
        </div>
      </button>
    </div>
  );
};

export default Tab;
